import { buildI18n, resolveFieldLabel } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import {
  type FinancialFieldDefinition,
  type FinancialSituationStatusDef,
  type FinancialSituationStatusThreshold,
  type FsClientType,
  fieldAppliesTo,
} from "@repo/shared/types";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import { ApiError } from "@/lib/api/_client";
import { useUpdateFinancialSituationModel } from "@/lib/hooks/use-financial-situation-models";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./derived-statuses-editor.i18n";

type SourceAccounts = { person: boolean; company: boolean; group: boolean };

const NO_ACCOUNTS: SourceAccounts = { person: false, company: false, group: false };

/** Client type → its key in `sourceFields` / `sourceAccounts`. */
const SOURCE_ACCOUNTS_KEY = {
  Person: "person",
  Company: "company",
  Group: "group",
} as const satisfies Record<FsClientType, keyof SourceAccounts>;

/** Stored statuses written before QL-546 have no `sourceAccounts`: all off. */
function toSourceAccounts(stored: FinancialSituationStatusDef["sourceAccounts"]): SourceAccounts {
  return {
    person: stored?.person === true,
    company: stored?.company === true,
    group: stored?.group === true,
  };
}

export type FieldGroups = {
  income: FinancialFieldDefinition[];
  assets: FinancialFieldDefinition[];
  liabilities: FinancialFieldDefinition[];
  commitments: FinancialFieldDefinition[];
};

type DerivedStatusesEditorProps = {
  modelId: string;
  statuses: FinancialSituationStatusDef[];
  fieldGroups: FieldGroups;
};

export function DerivedStatusesEditor({
  modelId,
  statuses,
  fieldGroups,
}: DerivedStatusesEditorProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);
  const updateModel = useUpdateFinancialSituationModel();
  const isPending = updateModel.isPending;

  const getLabel = (i18n: LocalizedString) => localize(i18n);

  // Build i18n object covering the user's language plus every tenant-supported
  // language — storing only the creator's language leaves other locales blank.

  // Dialog state (shared for create + edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [formLabel, setFormLabel] = useState("");
  const [formLabelI18n, setFormLabelI18n] = useState<LocalizedString>({});
  const [formPersonFields, setFormPersonFields] = useState<string[]>([]);
  const [formCompanyFields, setFormCompanyFields] = useState<string[]>([]);
  const [formGroupFields, setFormGroupFields] = useState<string[]>([]);
  // QL-546 — per client type, also sum the client's synced account values.
  const [formAccounts, setFormAccounts] = useState<SourceAccounts>(NO_ACCOUNTS);
  const [formThresholds, setFormThresholds] = useState<FinancialSituationStatusThreshold[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<string | null>(null);

  const saveStatuses = (next: FinancialSituationStatusDef[], onDone?: () => void) => {
    updateModel.mutate(
      { id: modelId, data: { statuses: next } },
      {
        onSuccess: () => {
          toast.success(t.saved);
          onDone?.();
        },
        onError: (err) => {
          toast.error(t.error, {
            description: err instanceof ApiError ? err.message : t.failedToSave,
          });
        },
      },
    );
  };

  const openCreate = () => {
    setEditingKey(null);
    setFormLabel("");
    setFormLabelI18n({});
    setFormPersonFields([]);
    setFormCompanyFields([]);
    setFormGroupFields([]);
    setFormAccounts(NO_ACCOUNTS);
    setFormThresholds([{ value: "", minValue: 0 }]);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (def: FinancialSituationStatusDef) => {
    setEditingKey(def.key);
    setFormLabel(getLabel(def.labelI18n));
    setFormLabelI18n(def.labelI18n);
    setFormPersonFields(def.sourceFields?.person ?? []);
    setFormCompanyFields(def.sourceFields?.company ?? []);
    // QL-140 — statuses stored before the group migration have no group list.
    setFormGroupFields(def.sourceFields?.group ?? []);
    setFormAccounts(toSourceAccounts(def.sourceAccounts));
    setFormThresholds(def.thresholds?.length ? def.thresholds : [{ value: "", minValue: 0 }]);
    setFormError(null);
    setDialogOpen(true);
  };

  // A status is only meaningful with a label and at least one named threshold —
  // the same two rules handleSubmit enforces, hoisted so the Save button can be
  // gated on them instead of answering the first click with an error (QL-143).
  const trimmedLabel = formLabel.trim();
  const normalisedThresholds = formThresholds
    .map((th) => ({ value: th.value.trim(), minValue: Number(th.minValue) || 0 }))
    .filter((th) => th.value);
  const isFormValid = !!trimmedLabel && normalisedThresholds.length > 0;

  // On edit the dialog opens populated and therefore valid, so it also has to
  // differ from the stored status before Save means anything.
  const editingDef = editingKey ? statuses.find((def) => def.key === editingKey) : undefined;
  const isFormDirty =
    !editingDef ||
    trimmedLabel !== getLabel(editingDef.labelI18n) ||
    JSON.stringify(formPersonFields) !== JSON.stringify(editingDef.sourceFields?.person ?? []) ||
    JSON.stringify(formCompanyFields) !== JSON.stringify(editingDef.sourceFields?.company ?? []) ||
    JSON.stringify(formGroupFields) !== JSON.stringify(editingDef.sourceFields?.group ?? []) ||
    JSON.stringify(formAccounts) !== JSON.stringify(toSourceAccounts(editingDef.sourceAccounts)) ||
    JSON.stringify(normalisedThresholds) !== JSON.stringify(editingDef.thresholds ?? []);

  const fieldGroupList: { key: keyof FieldGroups; label: string }[] = [
    { key: "income", label: t.income },
    { key: "assets", label: t.assets },
    { key: "liabilities", label: t.liabilities },
    { key: "commitments", label: t.commitments },
  ];

  const allFields = fieldGroupList.flatMap((group) => fieldGroups[group.key]);
  const applicableIds = (ids: string[], clientType: FsClientType) =>
    ids.filter((id) => {
      const field = allFields.find((f) => f.id === id);
      return field !== undefined && fieldAppliesTo(field, clientType);
    });

  const handleSubmit = () => {
    // The status key stays the stable internal identifier used by platform
    // conditions — auto-generated for new statuses, never edited by the user.
    const key = editingKey ?? crypto.randomUUID();
    const label = trimmedLabel;
    if (!label) {
      setFormError(t.labelRequired);
      return;
    }
    const thresholds = normalisedThresholds;
    if (thresholds.length === 0) {
      setFormError(t.thresholdRequired);
      return;
    }
    setFormError(null);

    const def: FinancialSituationStatusDef = {
      key,
      // Merge existing i18n with the updated value for the current language.
      labelI18n: editingKey
        ? { ...formLabelI18n, [userLanguage]: label }
        : {
            ...buildI18n(label, userLanguage, tenantSupportedLanguages),
            ...formLabelI18n,
            [userLanguage]: label,
          },
      // Drop ids that don't belong to the list's client type — the filtered
      // picker never shows them, and this is how a legacy misconfigured status
      // gets cleaned up on re-save (the server rejects such ids).
      sourceFields: {
        person: applicableIds(formPersonFields, "Person"),
        company: applicableIds(formCompanyFields, "Company"),
        group: applicableIds(formGroupFields, "Group"),
      },
      sourceAccounts: formAccounts,
      thresholds,
    };

    const next = editingKey
      ? statuses.map((existing) => (existing.key === editingKey ? def : existing))
      : [...statuses, def];
    saveStatuses(next, () => setDialogOpen(false));
  };

  const handleTranslationSave = (def: FinancialSituationStatusDef, labelI18n: LocalizedString) => {
    saveStatuses(
      statuses.map((existing) =>
        existing.key === def.key ? { ...existing, labelI18n } : existing,
      ),
    );
  };

  const handleDelete = () => {
    if (!statusToDelete) return;
    saveStatuses(
      statuses.filter((def) => def.key !== statusToDelete),
      () => setStatusToDelete(null),
    );
  };

  const toggleField = (
    list: string[],
    setList: (next: string[]) => void,
    fieldId: string,
    checked: boolean,
  ) => {
    setList(checked ? [...new Set([...list, fieldId])] : list.filter((id) => id !== fieldId));
  };

  const fieldLabel = (field: FinancialFieldDefinition) =>
    resolveFieldLabel(field, userLanguage) || field.id;

  // Each column lists only the fields tagged for its client type — a field
  // belongs to exactly one type, and a wrong-type id can never match an entry.
  const renderFieldPicker = (
    clientType: FsClientType,
    selected: string[],
    setSelected: (next: string[]) => void,
  ) => {
    const accountsKey = SOURCE_ACCOUNTS_KEY[clientType];
    const accountsId = `${clientType}-accounts`;
    const groups = fieldGroupList
      .map((group) => ({
        ...group,
        fields: fieldGroups[group.key].filter((field) => fieldAppliesTo(field, clientType)),
      }))
      .filter((group) => group.fields.length > 0);
    return (
      <div className="space-y-3 border rounded-lg p-3 max-h-64 overflow-y-auto">
        {/* QL-546 — the client's synced accounts, summed like a source field.
            Shown first and for every type: accounts exist even when the type
            has no fields configured. */}
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">{t.accountsGroup}</div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={accountsId}
              checked={formAccounts[accountsKey]}
              onCheckedChange={(checked) =>
                setFormAccounts({ ...formAccounts, [accountsKey]: checked === true })
              }
            />
            <Label htmlFor={accountsId} className="text-sm font-normal cursor-pointer">
              {t.accounts}
            </Label>
          </div>
        </div>
        {groups.length === 0 && (
          <p className="text-xs text-muted-foreground">{t.noFieldsForType}</p>
        )}
        {groups.map((group) => (
          <div key={group.key} className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">{group.label}</div>
            {group.fields.map((field) => (
              <div key={field.id} className="flex items-center gap-2">
                <Checkbox
                  id={`${clientType}-${group.key}-${field.id}`}
                  checked={selected.includes(field.id)}
                  onCheckedChange={(checked) =>
                    toggleField(selected, setSelected, field.id, checked === true)
                  }
                />
                <Label
                  htmlFor={`${clientType}-${group.key}-${field.id}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {fieldLabel(field)}
                </Label>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // e.g. "4 field(s) + accounts (persons)".
  const sourceSummary = (
    def: FinancialSituationStatusDef,
    key: keyof SourceAccounts,
    typeLabel: string,
  ) => {
    const count = def.sourceFields?.[key]?.length ?? 0;
    const accounts = def.sourceAccounts?.[key] ? ` ${t.plusAccounts}` : "";
    return `${count} ${t.fields}${accounts} (${typeLabel})`;
  };

  const thresholdsSummary = (def: FinancialSituationStatusDef) =>
    [...(def.thresholds ?? [])]
      .sort((a, b) => a.minValue - b.minValue)
      .map((th) => `${th.value}/${formatNumber(th.minValue)}`)
      .join(", ");

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle>{t.derivedStatuses}</CardTitle>
              <CardDescription>{t.derivedStatusesDescription}</CardDescription>
            </div>
            <Button onClick={openCreate} disabled={isPending}>
              <Plus className="h-4 w-4 mr-2" />
              {t.addStatus}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {statuses.length === 0 ? (
            <EmptyState>{t.noStatuses}</EmptyState>
          ) : (
            <Table variant="data">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.label}</TableHead>
                  <TableHead>{t.sourceFields}</TableHead>
                  <TableHead>{t.thresholds}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statuses.map((def) => (
                  <TableRow key={def.key}>
                    <TableCell>
                      <span className="font-medium">{getLabel(def.labelI18n)}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {sourceSummary(def, "person", t.personFields)},{" "}
                      {sourceSummary(def, "company", t.companyFields)},{" "}
                      {sourceSummary(def, "group", t.groupFields)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {thresholdsSummary(def)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          tooltip={t.editStatus}
                          variant="ghost"
                          onClick={() => openEdit(def)}
                          disabled={isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <TranslationButton
                          value={def.labelI18n}
                          onSave={(labelI18n) => handleTranslationSave(def, labelI18n)}
                          supportedLanguages={tenantSupportedLanguages}
                          disabled={isPending}
                        />
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          onClick={() => {
                            setStatusToDelete(def.key);
                            setConfirmDeleteOpen(true);
                          }}
                          disabled={isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingKey ? t.editStatus : t.addStatus}</DialogTitle>
            <DialogDescription>{t.dialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="status-label">{t.label}</Label>
              <Input
                id="status-label"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder={t.labelPlaceholder}
                disabled={isPending}
              />
            </div>

            {/* QL-140 — one source list per client type; a group's status is
                resolved from the group entries. */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t.sourceFieldsPerson}</Label>
                {renderFieldPicker("Person", formPersonFields, setFormPersonFields)}
              </div>
              <div className="space-y-2">
                <Label>{t.sourceFieldsCompany}</Label>
                {renderFieldPicker("Company", formCompanyFields, setFormCompanyFields)}
              </div>
              <div className="space-y-2">
                <Label>{t.sourceFieldsGroup}</Label>
                {renderFieldPicker("Group", formGroupFields, setFormGroupFields)}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t.sourceFieldsHint}</p>

            <div className="space-y-2">
              <Label>{t.thresholds}</Label>
              <div className="space-y-2">
                {formThresholds.map((threshold, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional editor state
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={threshold.value}
                      onChange={(e) =>
                        setFormThresholds(
                          formThresholds.map((th, i) =>
                            i === index ? { ...th, value: e.target.value } : th,
                          ),
                        )
                      }
                      placeholder={t.thresholdValuePlaceholder}
                      aria-label={t.thresholdValue}
                      disabled={isPending}
                    />
                    <Input
                      type="number"
                      value={threshold.minValue}
                      onChange={(e) =>
                        setFormThresholds(
                          formThresholds.map((th, i) =>
                            i === index ? { ...th, minValue: Number(e.target.value) || 0 } : th,
                          ),
                        )
                      }
                      placeholder="0"
                      aria-label={t.thresholdMin}
                      disabled={isPending}
                    />
                    <Button
                      size="icon"
                      tooltip={t.delete}
                      variant="ghost"
                      onClick={() =>
                        setFormThresholds(formThresholds.filter((_, i) => i !== index))
                      }
                      disabled={isPending || formThresholds.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFormThresholds([...formThresholds, { value: "", minValue: 0 }])}
                disabled={isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t.addThreshold}
              </Button>
              <p className="text-xs text-muted-foreground">{t.thresholdsHint}</p>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || !isFormDirty}
              loading={isPending}
            >
              {editingKey ? t.save : t.addStatus}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteStatus}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />
    </>
  );
}
